import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { ApiFailure, EiaClient } from '../api/client'
import { useAuth } from '../auth/useAuth'
import { EngagementStream } from '../stream/sse'
import type { ConnectionStatus } from '../stream/sse'
import { empty, reduce } from '../stream/reducer'
import type { EngagementView } from '../stream/reducer'
import type { GateAnswer } from '../components/gates/GateShell'
import { ErrorNotice } from './Workspace'
import { Topbar } from '../components/chrome/Topbar'
import { Rail } from '../components/engagement/Rail'
import { Conversation } from '../components/engagement/Conversation'
import { SpecDrawer } from '../components/engagement/SpecDrawer'
import { ArtefactCard } from '../components/report/Artefacts'
import { ReportOverlay } from '../components/report/ReportOverlay'
import type { Artefact } from '../api/types'

/**
 * One engagement. `04 §4.2` — THE DEEP-LINK TARGET, and `#gate` is what a notification links to.
 *
 * <p><b>THREE ZONES</b>, from `Frontend/engagement.html` and `Specification/08 §2`: the process
 * rail, the conversation, and the specification. The rail is always visible and never behind a tab;
 * the specification is collapsed to a 34px edge tab and expands IN FLOW to 420px, so nothing is
 * ever hidden behind it.</p>
 */
export function Engagement(): React.JSX.Element {
  const { engagementId = '' } = useParams()
  const { token, stale } = useAuth()
  const client = useMemo(() => new EiaClient(token), [token])

  const [view, setView] = useState<EngagementView>(empty)
  const [status, setStatus] = useState<ConnectionStatus>('connecting')
  const [error, setError] = useState<ApiFailure | null>(null)
  const [sending, setSending] = useState(false)
  const [artefacts, setArtefacts] = useState<Artefact[]>([])
  const [reportOpen, setReportOpen] = useState(false)
  const [specOpen, setSpecOpen] = useState(false)
  const [snapshot, setSnapshot] = useState<Record<string, unknown> | null>(null)
  const [elapsed, setElapsed] = useState<number | null>(null)
  const since = useRef<number | null>(null)

  // THE VIEW IS STAMPED ON THE BODY, because the lifted stylesheets scope their layout rules to it.
  // One document, three views, and `overflow:hidden` belongs to exactly one of them.
  useEffect(() => {
    document.body.dataset['view'] = 'engagement'
    return () => { delete document.body.dataset['view'] }
  }, [])

  // THE DRAWER'S VISIBILITY IS THE BODY'S `data-spec`, exactly as the mockup drives it. The rules
  // that hide the tab when the drawer is open, and the drawer when it is not, both live in
  // `engagement.css` and both key on this.
  useEffect(() => {
    document.body.dataset['spec'] = specOpen ? 'open' : 'closed'
    return () => { delete document.body.dataset['spec'] }
  }, [specOpen])

  // THE ARTEFACTS ARE FETCHED WHEN THE RUN REACHES A TERMINAL STATE, not polled. Nothing produces
  // one mid-run that a reader could open, and a list that refreshed every few seconds would spend
  // requests to show the same empty set.
  useEffect(() => {
    // EITHER SIGNAL IS ENOUGH, AND THEY COVER DIFFERENT CASES. The `artefacts` frame arrives live
    // at phase 11, BEFORE the sign gate — which is the point of publishing there, so a reader can
    // open the report while deciding whether to sign it. The terminal state covers a page opened
    // after the fact, where that frame is in the replayed backlog and the fetch is the reliable
    // read of what is actually in the container now.
    if (view.terminal === null && view.artefacts.length === 0) return

    void client.artefacts(engagementId).then(setArtefacts).catch(() => {
      // An artefact list that cannot be read is not worth interrupting the page for: the terminal
      // state and its reason are already rendered, and those are what the reader came for.
    })
  }, [view.terminal, view.artefacts.length, client, engagementId])

  useEffect(() => {
    if (engagementId.length === 0) return

    const stream = new EngagementStream(
      `/api/engagements/${encodeURIComponent(engagementId)}/events`,
      {
        token,
        onStatus: setStatus,
        onFrame: (frame) => setView((current) => reduce(current, frame)),
      })

    void stream.start()
    return () => stream.stop()
  }, [engagementId, token])

  // ONE LIVE LINE WITH THE ELAPSED TIME IN THE CURRENT PHASE (`§6.5`). It restarts on a phase
  // entry, because the number means "how long has this been going", not "how old is this page".
  useEffect(() => {
    since.current = Date.now()
    setElapsed(0)
    const tick = window.setInterval(() => {
      if (since.current !== null) setElapsed((Date.now() - since.current) / 1000)
    }, 1000)
    return () => window.clearInterval(tick)
  }, [view.phase, view.entryOrdinal])

  /**
   * THE CLIENT MUST NOT SILENTLY RENDER A PARTIAL STREAM (`AC-SSE-5`).
   *
   * When the server says the replay could not reach back far enough, the page rebuilds from the
   * bounded state snapshot rather than showing whatever happened to arrive.
   */
  useEffect(() => {
    if (!view.replayIncomplete) return

    void client.state(engagementId)
      .then((state) => {
        setSnapshot(specificationOf(state))
        setView((current) => ({ ...current, replayIncomplete: false }))
      })
      .catch((failure: unknown) => {
        if (failure instanceof ApiFailure) setError(failure)
      })
  }, [view.replayIncomplete, client, engagementId])

  // THE SNAPSHOT IS READ WHEN THE DRAWER IS OPENED, and not before. It is the values behind the
  // committed keys, which nobody is looking at while the drawer is a 34px tab.
  useEffect(() => {
    if (!specOpen || snapshot !== null) return

    void client.state(engagementId)
      .then((state) => setSnapshot(specificationOf(state)))
      .catch(() => {
        // The key list and the decisions come from the stream and are already right. A snapshot
        // that cannot be read costs the JSON pane, not the panel.
      })
  }, [specOpen, snapshot, client, engagementId])

  const answer = useCallback(async (gate: Record<string, unknown>, decision: GateAnswer) => {
    const gateId = String(gate['gate_id'] ?? gate['gateId'] ?? '')
    setSending(true)
    try {
      // WHATEVER THE CARD COLLECTED, and nothing this page invents. A clarify card carries one
      // `answers` entry per question; a binding card carries a note. `approved` cannot be sent
      // from here at all — the client has no field for it.
      await client.answer(engagementId, gateId, decision.decision, {
        ...(decision.extra ?? {}),
        ...(decision.note === undefined ? {} : { note: decision.note }),
      })
      setError(null)
    } catch (failure) {
      // A 409 IS NOT AN ERROR TO APOLOGISE FOR. Two tabs on one engagement: the first answer won,
      // and the `state` event that follows re-renders this page. The notice says what happened.
      if (failure instanceof ApiFailure) setError(failure)
    } finally {
      setSending(false)
    }
  }, [client, engagementId])

  // THE QUESTION, WHICH IS THE ONE THING THE PAGE MUST SHOW. It came from the specification
  // snapshot, which is fetched only when the drawer opens or a replay reports a gap — so the ASKED
  // band and the opening turn rendered the ENGAGEMENT ID on a page whose whole subject is what was
  // asked. The summary comes off the engagement itself, which is one request on mount.
  const [ask, setAsk] = useState<string | null>(null)
  const [summary, setSummary] = useState<string | null>(null)

  useEffect(() => {
    if (engagementId.length === 0) return

    void client.detail(engagementId)
      .then((detail) => {
        const row = detail as Record<string, unknown>

        // THE QUESTION WHOLE, and the summary separately. The pinned band truncates by design —
        // it is one line that survives scrolling — but the OPENING TURN is the question itself,
        // and an ellipsis there hides the thing the page is about with no way to expand it.
        if (typeof row['prompt'] === 'string' && row['prompt'].length > 0) setAsk(row['prompt'])
        if (typeof row['askSummary'] === 'string') setSummary(row['askSummary'])
      })
      .catch(() => {
        // The stream carries everything else. A question that cannot be read is a blank band, not
        // a reason to stop rendering the engagement.
      })
  }, [client, engagementId])

  // THE QUESTION LIVES AT `provenance_of_this_spec.prompt`, VERBATIM — not at the top level, and
  // not on the engagement block. This read `snapshot['prompt']`, which no specification has, so it
  // was always undefined and the question rendered only because `client.detail()`'s fallback
  // carried it (B-170, and the same misreading as B-166).
  const fromSpec = (snapshot?.['provenance_of_this_spec'] as { prompt?: unknown } | undefined)?.prompt
  const question = typeof fromSpec === 'string' ? fromSpec : ask

  return (
    <>
      <Topbar engagementId={engagementId} connection={status} stale={stale} />

      <div className="work">
        <Rail view={view} elapsed={elapsed} />

        <Conversation view={view} ask={question ?? summary ?? engagementId} busy={sending}
                      onAnswer={(gate, decision) => void answer(gate, decision)}>
          {error && <ErrorNotice failure={error} />}

          {view.gaps.length > 0 && (
            <div className="inote" role="alert">
              <p className="mlabel">Incomplete</p>
              <p style={{ margin: 0 }}>
                {view.gaps.length} event{view.gaps.length === 1 ? '' : 's'} did not arrive. What is
                above is incomplete, and reloading will rebuild it.
              </p>
            </div>
          )}

          {view.results.length > 0 && (
            <section className="turn">
              <div className="body">
                <table className="results">
                  <caption>Results</caption>
                  <thead>
                    <tr><th>Figure</th><th className="num">Value</th><th>Unit</th></tr>
                  </thead>
                  <tbody>
                    {view.results.map((r, i) => (
                      <tr key={i}>
                        <td>{String(r['figure'] ?? '')}</td>
                        <td className="num">{String(r['value'] ?? '')}</td>
                        <td>{String(r['unit'] ?? '')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          <ArtefactCard client={client} engagementId={engagementId} artefacts={artefacts}
                        specSha256={view.frozen ? String(view.frozen['sha256'] ?? '') : null}
                        onOpenReport={() => setReportOpen(true)} />

          {view.terminal && (
            <div className="inote" role="status">
              <p className="mlabel">{view.lifecycle === 'signed' ? 'Signed' : 'Stopped'}</p>
              <p style={{ margin: 0 }}>{String(view.terminal['reason'] ?? '')}</p>
              {/* A HALT NAMES WHAT THE USER CAN DO INSTEAD. A refusal with no route forward is a
                  wall. */}
              {view.terminal['route_forward'] !== undefined
                && view.terminal['route_forward'] !== null && (
                <p style={{ margin: '7px 0 0' }}>
                  <b>Route forward:</b> {String(view.terminal['route_forward'])}
                </p>
              )}
            </div>
          )}
        </Conversation>

        <SpecDrawer view={view} open={specOpen} snapshot={snapshot}
                    onOpen={() => setSpecOpen(true)} onClose={() => setSpecOpen(false)} />
      </div>

      {reportOpen && (
        <ReportOverlay client={client} engagementId={engagementId}
                       onClose={() => setReportOpen(false)} />
      )}
    </>
  )
}

/**
 * The SPECIFICATION out of the bounded snapshot.
 *
 * <p><b>`/state` RETURNS FOUR SIBLINGS</b> — `{engagement, specification, specVersion, seq}` — and
 * this page stored the whole envelope and then read specification keys off it. `snapshot['triage']`
 * is `undefined` for every key, because `triage` lives under `specification`; `summarise(undefined)`
 * returns the string <b>"committed"</b>, so all fifteen rows of the drawer rendered an identical
 * plausible-looking word and the panel's promise — "the values behind the committed keys" — was
 * never kept (B-170).</p>
 *
 * <p>There is no fallback to the flat shape. Accepting both is how a misreading survives.</p>
 */
function specificationOf(state: unknown): Record<string, unknown> | null {
  const spec = (state as { specification?: unknown } | null)?.specification
  return spec !== null && typeof spec === 'object' ? spec as Record<string, unknown> : null
}
